/**
 * Post-auth deep-link redirect helper — FE #199.
 *
 * Problem this solves
 * -------------------
 * The app's authenticated routes (`/notifications`, `/groups/:id`,
 * `/item/:id`, `/my-shares`, …) all live inside the `MainApp` stack,
 * which is ONLY mounted when `isAuthenticated === true`. When a user
 * clicks an email or push deep-link while signed-out, React Navigation
 * tries to match the URL against the unauth stack (Login / Register
 * / ForgotPassword), fails, and falls back to the first screen
 * (Login). After the user signs in, the URL has been clobbered to
 * `/home` (or `/login`) and the original target is lost — the user
 * lands on Dashboard with no context of what they were trying to
 * reach.
 *
 * Approach
 * --------
 * Mirror the `pendingInvite.ts` pattern: stash the original
 * pathname + search to sessionStorage at app boot (before the React
 * tree mounts), then replay it via `window.history.replaceState`
 * once auth resolves successfully — React Navigation's linking
 * config picks the URL up and routes correctly.
 *
 * Why sessionStorage (not localStorage)?
 * - The pending target only matters for THIS browser session. If the
 *   user closes the tab and returns later, falling back to Home is
 *   the right behavior.
 * - localStorage would persist a stale target across days.
 *
 * Open-redirect guard
 * -------------------
 * We only honor a stashed target if it parses as a same-origin
 * relative path:
 *   - Must start with `/`
 *   - Must NOT start with `//` (which would be protocol-relative
 *     and could escape to attacker.com)
 *   - Must NOT contain `://` anywhere
 *
 * Auth routes themselves never get stashed — we don't want to bounce
 * a user back to `/login` after they just signed in.
 */

const KEY = '@founsi/post_boot_redirect';

const AUTH_PATHS = new Set<string>([
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
]);

function isAuthPath(path: string): boolean {
  // Match prefix so `/login?next=...` is also treated as auth.
  for (const p of AUTH_PATHS) {
    if (path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`)) {
      return true;
    }
  }
  return false;
}

function isSameOriginRelative(target: string): boolean {
  if (typeof target !== 'string' || target.length === 0) return false;
  if (!target.startsWith('/')) return false;
  if (target.startsWith('//')) return false;
  if (target.includes('://')) return false;
  return true;
}

function sessionStorageOrNull(): Storage | null {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      return window.sessionStorage;
    }
  } catch {
    /* private mode / SSR / native runtime — fall through */
  }
  return null;
}

export function setPostBootRedirect(target: string): void {
  if (!isSameOriginRelative(target)) return;
  const ss = sessionStorageOrNull();
  if (!ss) return;
  try {
    ss.setItem(KEY, target);
  } catch {
    /* storage full / disabled — silently drop. Worst case the user
       lands on Home and re-clicks the link. */
  }
}

export function getPostBootRedirect(): string | null {
  const ss = sessionStorageOrNull();
  if (!ss) return null;
  try {
    const v = ss.getItem(KEY);
    if (v == null) return null;
    return isSameOriginRelative(v) ? v : null;
  } catch {
    return null;
  }
}

export function clearPostBootRedirect(): void {
  const ss = sessionStorageOrNull();
  if (!ss) return;
  try {
    ss.removeItem(KEY);
  } catch {
    /* noop */
  }
}

/**
 * Called synchronously from `App.tsx` BEFORE the React tree mounts.
 * Captures the original deep-link path so we can replay it once
 * auth resolves.
 *
 * Skipped for:
 *   - Auth paths (login/register/etc) — don't bounce back to them
 *   - The home path itself — nothing to restore
 *   - Invite paths — `pendingInvite.ts` already handles them
 *   - Share-claim paths — already auth-aware in the linking config
 *
 * Returns the captured target (or null) for tests + logging.
 */
export function bootstrapPostBootRedirectFromUrl(): string | null {
  try {
    if (typeof window === 'undefined' || !window.location) return null;
    const pathname = window.location.pathname || '/';
    const search = window.location.search || '';
    const target = `${pathname}${search}`;
    if (!isSameOriginRelative(target)) return null;
    // Skip home + auth — no redirect needed.
    if (pathname === '/' || pathname === '/home') return null;
    if (isAuthPath(pathname)) return null;
    // Skip routes that have their own pre-auth stash mechanism so we
    // don't trigger redundant work or conflict with their handlers.
    if (pathname.startsWith('/invite/')) return null;
    if (pathname.startsWith('/share/')) return null;
    setPostBootRedirect(target);
    return target;
  } catch {
    return null;
  }
}

/**
 * Called once post-auth to replay the stashed target by mutating
 * `window.location` via history.replaceState. React Navigation's
 * linking config picks up the path change and routes accordingly.
 *
 * Clears the stash on success or terminal failure so a future
 * auth cycle starts clean.
 */
export function consumePostBootRedirect(): string | null {
  const target = getPostBootRedirect();
  clearPostBootRedirect();
  if (!target) return null;
  try {
    if (typeof window === 'undefined' || !window.history) return target;
    const current = window.location.pathname + window.location.search;
    if (current === target) return target; // Already there.
    // FE #156 — replaceState alone doesn't trigger React Navigation's
    // linking observer. RN-Web's linker subscribes to the `popstate`
    // event; we dispatch it manually after the URL mutation so the
    // linker picks up the new path + routes us into MainApp.
    // Without this dispatch the URL changes silently but the screen
    // stays on Home (regression that hit /notifications, /community,
    // /scans, /groups/X — every authenticated deep-link).
    window.history.replaceState(window.history.state, '', target);
    window.dispatchEvent(new PopStateEvent('popstate'));
    return target;
  } catch {
    return target;
  }
}
